/**
 * ⭐ `R4-UIM1` · **B15 — EL REDONDEO Y EL FORMATO DE UNA CELDA DE MÉTRICAS.**
 *
 * `PRD_R4.md:1733-1739` (§Formato de cada celda, §CSV de resultados):
 *
 *   · Enviados, Sesiones y Mensajes de las personas ⇒ el número solo («11»).
 *   · Entregados, Leídos y el grupo «Personas» salvo la comparación ⇒ «N · P %»
 *     con el porcentaje redondeado al entero más cercano («7 · 64 %»); con base
 *     0, «N · —».
 *   · Interacción positiva vs. periodo anterior ⇒ el entero con su signo:
 *     «+67 %», «-20 %» o «0 %»; «—» sin periodo anterior o con el anterior en 0.
 *   · Promedios ⇒ un decimal con coma («2,3»); «—» si el divisor es 0.
 *   · «Los redondeos van al más cercano, y la mitad hacia arriba. Los números de
 *     más de tres cifras llevan punto de miles.»
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ⛔⛔ POR QUÉ LA REGLA DE B15 ESTÁ ESPEJADA ACÁ Y NO IMPORTADA — MEDIDO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * El `guard` B15 de `specs/R4-F3-metricas-apps/etapas/r4-uim1.md` pide que «las
 * celdas usen la función pura única del contrato» — `formatearMetrica` de
 * `contracts/analytics/index.ts:1599`—, y tiene toda la razón en el fondo: dos
 * reglas de redondeo dan «64 %» en la pantalla y «63» en el CSV para la misma
 * fracción, que es exactamente lo que B15 cerró.
 *
 * ⛔ **Pero esta aplicación NO PUEDE IMPORTAR UN VALOR DE `contracts/`, y no es
 * una opinión: lo sostiene la herramienta.** `vite.config.ts:10-15`, textual:
 * *«No hay alias hacia `contracts/` acá, y es deliberado … TypeScript resuelve
 * `@contracts/*` para los tipos, el bundler no. Un `import type` lo borra
 * `esbuild` antes de resolver nada; un import de VALOR desde `contracts/` —que
 * podría arrastrar una referencia a `modules/**`— hace fallar el build.»*
 *
 * ⛔ **Y la medición:** `grep -rn 'from "@contracts' src/ | grep -v "import type"`
 * ⇒ **0** sobre **51** imports de contrato. **Las 51 son `import type`.** No hay
 * un solo precedente de import de valor en toda la consola.
 *
 * ⚠️ **El modo de falla de desobedecerlo es CIEGO, y es lo que decide.** El
 * `tsc` de la SPA resuelve `@contracts/*` por `tsconfig.json:86` y daría VERDE
 * con el import de valor puesto; `bun test pruebas/` también, porque los
 * exámenes no pasan por el bundler. **Lo único que se rompería es `vite build`**
 * — y esta etapa tiene prohibido correrlo (el sourcemap de `dist/` hace fallar
 * `PII1` cerrado). Un import de valor acá es una bomba que ningún instrumento de
 * esta etapa puede ver estallar.
 *
 * ⇒ **La regla se ESPEJA, y la copia se ATA con un examen.** `redondearMitadArriba`
 * y `conPuntoDeMiles` de abajo son los de `contracts/analytics/index.ts:1577-1586`
 * **carácter por carácter**, y `pruebas/r4-uim1-formato.test.ts` importa
 * `formatearMetrica` del contrato POR RUTA RELATIVA —los exámenes sí pueden, es
 * lo que `aw12-metricas.test.tsx:30` ya hace con `fake.ts`— y afirma la IGUALDAD
 * de las dos salidas sobre una tabla de casos. Una divergencia futura no se
 * «espera que no pase»: pone rojo un caso.
 *
 * ⚠️ **Lo que ese examen NO puede ver**, dicho acá y no descubierto después: un
 * cambio en `formatearMetrica` que la tabla de casos no ejercite. La tabla
 * incluye los dos bordes del redondeo (`.5` arriba en positivo y en negativo),
 * el `null`, el punto de miles y las cuatro clases, que es lo que la función
 * tiene.
 *
 * ⛔ **ESTO ES UNA DEUDA CON DUEÑO, NO UNA SOLUCIÓN.** La única forma de que la
 * regla viva UNA sola vez es que `formatearMetrica` baje a un archivo que la SPA
 * pueda importar sin alias (o que `vite.config.ts` gane el alias y la regla de
 * arquitectura se revise). Las dos cosas están **fuera del territorio de
 * `R4-UIM1`** (`contracts/**` y la raíz de la app). **Dueño: TL.**
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ⛔ Y LO QUE ESTA PANTALLA SÍ AGREGA, QUE EL CONTRATO NO TIENE
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `formatearMetrica` es la regla del NÚMERO. El `%`, el punto medio y el `+` de
 * la delta son de la PANTALLA y el contrato los deja afuera a propósito
 * (`PRD_R4.md:1733`: el CSV va *«sin el signo «%»»* y la comparación *«sin
 * «+»»*). ⇒ se agregan acá, **sobre la salida ya redondeada**, nunca redondeando
 * de nuevo:
 *
 *   `textoDeDelta(0.67)` → `formatearMetrica(67, "delta", "pantalla")` → `"67"`
 *                        → `"+67 %"`
 *
 * ⚠️ El `+` se decide mirando el TEXTO redondeado y no el número crudo. Con
 * `value = 0.004`, el crudo es positivo pero el redondeo da `"0"`: el PRD pide
 * «0 %» y no «+0 %». Mirar el crudo pondría el signo que el PRD no quiere.
 *
 * ⛔ **El `× 100` sigue siendo de presentación** (⟦rastro · `CP-F4-007`⟧, que
 * esta etapa conserva): `Percentage.value` es `numerator / denominator` por
 * contrato, fracción cruda, y `modules/analytics/src/exportacion/csv.ts` la
 * escribe sin el factor. Multiplicar en el módulo arrastraría el CSV.
 */

import type { ErrorsResponseBody, MetricsResponseBody } from "@contracts/analytics-api/http.ts"

/** La fila de métricas tal como viaja (`EnElCable<PeriodMetricRow>`). */
export type FilaDeMetricas = MetricsResponseBody["items"][number]
/** La fila de errores tal como viaja (`EnElCable<PeriodErrorRow>`). */
export type FilaDeErrores = ErrorsResponseBody["items"][number]
/** Las métricas de un periodo: la unión `conDerivacion | sinDerivacion` del contrato. */
export type MetricasDePeriodo = FilaDeMetricas["metrics"]
/** Un `MetricValue`: conteo y su porcentaje. */
export type ValorDeMetrica = MetricasDePeriodo["entregados"]
/** Un `Percentage`: numerador, denominador y el valor (`null` ⟺ denominador 0). */
export type Porcentaje = ValorDeMetrica["percent"]

/**
 * La clase de número, con los mismos cuatro literales que
 * `contracts/analytics/index.ts:1571` (`ClaseDeMetrica`).
 *
 * ⚠️ Se anota contra el tipo del contrato más abajo (`ClaseEspejada`), así que
 * el día que el contrato agregue una quinta clase este archivo no compila.
 */
export type ClaseDeMetrica = "entero" | "porcentaje" | "promedio" | "delta"

/** De dónde se formatea: la pantalla usa «—»; el CSV deja la celda vacía. */
export type SalidaDeMetrica = "pantalla" | "csv"

/**
 * ⛔ **EL TESTIGO DE TIPO, Y MIDE UNA SOLA DIRECCIÓN — LA OTRA LA MIDE EL EXAMEN.**
 *
 * `typeof import(...)` es una construcción de TIPO: `esbuild` la borra junto con
 * el resto de las anotaciones, así que **no mete a `contracts/` en el bundle**.
 * Es el mismo recurso con el que `borde-metricas.ts` ata sus rutas literales.
 *
 * ⚠️⚠️ **CORRECCIÓN ⟦ADR-0082 · 05/10/2026 · R4-UIM1 vuelta 2⟧.** Este bloque
 * decía: *«Si el contrato le agrega o le quita una clase o una salida, esta
 * asignación falla y el `tsc` de la SPA lo dice en esta línea»*. **Sobreafirmaba,
 * y lo medí:**
 *
 *   · La asignación de abajo es `local → contrato`, así que atrapa **una clase que
 *     el tipo LOCAL tenga y el contrato no**. Mutante `+ "quinta"` al tipo local
 *     ⇒ `TS2322` en esta línea. ✅ **Esa dirección SÍ.**
 *   · ⛔ **Pero NO se mueve si el contrato AGREGA una clase**: con el contrato en
 *     cinco literales y el local en cuatro, `local ⊆ contrato` sigue siendo cierto
 *     y esta línea pasa en verde.
 *
 * ⭐ **La otra dirección la cubre `pruebas/r4-uim1-formato.test.ts:162`**, y lo
 * verifiqué con su mutante: ahí la tabla `CASOS` está tipada con el
 * `ClaseDeMetrica` **del contrato** y se le pasa a la función **local**, así que
 * `contrato → local` se chequea en la llamada. Mutante «al tipo local le falta
 * `"delta"`» ⇒ `TS2345` en `:162` (y en `:196` y en `:223`).
 *
 * ⇒ **las dos líneas juntas fijan la IGUALDAD de las dos uniones; ninguna sola lo
 * hace.** Quien borre el examen se queda con la mitad y no se va a enterar.
 */
type ClaseDelContrato = Parameters<typeof import("@contracts/analytics/index.ts").formatearMetrica>[1]
type SalidaDelContrato = Parameters<typeof import("@contracts/analytics/index.ts").formatearMetrica>[2]
const _claseEspejada: ClaseDelContrato = "entero" as ClaseDeMetrica
const _salidaEspejada: SalidaDelContrato = "pantalla" as SalidaDeMetrica
void _claseEspejada
void _salidaEspejada

/**
 * Redondeo con la mitad hacia arriba —B15—, **en la magnitud y no en el signo**.
 *
 * ⛔ ESPEJO EXACTO de `contracts/analytics/index.ts:1577-1579`. `Math.round(-0.5)`
 * da `-0` (la mitad hacia +∞), que para un delta negativo redondea al revés: por
 * eso el signo se saca, se redondea la magnitud y se vuelve a poner.
 */
function redondearMitadArriba(valor: number): number {
  return Math.sign(valor) * Math.round(Math.abs(valor))
}

/**
 * Punto de miles, sin depender del locale del proceso.
 *
 * ⛔ ESPEJO EXACTO de `contracts/analytics/index.ts:1582-1584`. `toLocaleString`
 * usaría el locale de quien corre el proceso: el mismo número daría `1,234` en
 * una máquina y `1.234` en otra.
 */
function conPuntoDeMiles(entero: number): string {
  return String(entero).replace(/\B(?=(\d{3})+(?!\d))/g, ".")
}

/**
 * **B15 · LA REGLA DEL NÚMERO.** Espejo de
 * `contracts/analytics/index.ts:1599-1612`, atado por
 * `pruebas/r4-uim1-formato.test.ts`.
 *
 * ⚠️ El cuerpo es el del contrato sin una coma de diferencia. Si hay que
 * cambiarlo, se cambia el del contrato primero y el examen de igualdad trae el
 * cambio acá como un rojo.
 */
export function formatearMetrica(
  valor: number | null,
  clase: ClaseDeMetrica,
  salida: SalidaDeMetrica,
): string {
  if (valor === null) return salida === "pantalla" ? "—" : ""
  if (clase === "promedio") {
    const decimas = redondearMitadArriba(valor * 10) / 10
    return decimas.toFixed(1).replace(".", ",")
  }
  const entero = redondearMitadArriba(valor)
  const signo = entero < 0 ? "-" : ""
  return signo + conPuntoDeMiles(Math.abs(entero))
}

/** El «—» de la pantalla, UNA vez: lo piden la celda vacía, el promedio y la delta. */
export const SIN_DATO = "—"

/**
 * Un entero de la Tabla 1 (Enviados, Sesiones, Mensajes de las personas): el
 * número solo, con punto de miles. `PRD_R4.md:1734`.
 */
export function textoDeEntero(valor: number): string {
  return formatearMetrica(valor, "entero", "pantalla")
}

/**
 * Un porcentaje de celda: el entero + « %». `PRD_R4.md:1735`.
 *
 * ⛔ `value` es la FRACCIÓN cruda del contrato: el `× 100` es de presentación
 * (⟦rastro · `CP-F4-007`⟧) y se aplica ANTES de redondear, una sola vez.
 */
function textoDePorcentaje(percent: Porcentaje): string {
  if (percent.value === null) return SIN_DATO
  return `${formatearMetrica(percent.value * 100, "porcentaje", "pantalla")} %`
}

/**
 * Una celda «N · P %» del grupo «Personas» (y Entregados / Leídos).
 * `PRD_R4.md:1735`: *«el número, un punto medio y el porcentaje redondeado al
 * entero más cercano («7 · 64 %»). Si la base del porcentaje es 0, «N · —»»*.
 *
 * ⚠️ El separador es «·» (punto medio U+00B7) con un espacio a cada lado, que es
 * como el PRD lo escribe y como los CA lo citan («7 · 64 %»).
 */
export function textoDeMetricValue(metric: ValorDeMetrica): string {
  return `${textoDeEntero(metric.count)} · ${textoDePorcentaje(metric.percent)}`
}

/**
 * La celda «Interacción positiva vs. periodo anterior». `PRD_R4.md:1736`:
 * *«El cambio en porcentaje, redondeado al entero, con su signo: «+67 %»,
 * «-20 %» o «0 %». «—» en el periodo que no tiene periodo anterior o cuando el
 * número del anterior es 0»*.
 *
 * ⛔ Los DOS caminos a «—» son distintos y los dos están: `delta === null` (no
 * hay periodo anterior, ⟦delta-null-en-primer-periodo⟧) y `delta.value === null`
 * (el anterior está en 0, denominador 0). Tratar sólo uno dibuja «NaN %» en el
 * otro.
 */
export function textoDeDelta(delta: Porcentaje | null): string {
  if (delta === null || delta.value === null) return SIN_DATO
  const entero = formatearMetrica(delta.value * 100, "delta", "pantalla")
  /*
   * El `+` se decide sobre el TEXTO ya redondeado, no sobre el crudo: con
   * `value = 0.004` el crudo es positivo y el redondeo da `"0"`, y el PRD pide
   * «0 %», no «+0 %». Ver el docblock del encabezado.
   */
  const conSigno = entero.startsWith("-") || entero === "0" ? entero : `+${entero}`
  return `${conSigno} %`
}

/**
 * Un promedio (Promedio de interacciones positivas por persona, Sesiones por
 * persona). `PRD_R4.md:1737`: *«Un decimal con coma («2,3»). «—» si el divisor
 * es 0»* — y el contrato declara esos dos campos `number | null`, con `null`
 * exactamente cuando el divisor es 0.
 */
export function textoDePromedio(valor: number | null): string {
  return formatearMetrica(valor, "promedio", "pantalla")
}

/**
 * ⭐⭐ **D3 / B13 · UN DÍA SE REORDENA POR TEXTO. NUNCA UN `new Date`.**
 *
 * `R4-F3` RT 1 (`PRD_R4.md:1814`) y `contracts/analytics/index.ts:123`
 * (`DiaDeChile` · `FORMA_DEL_DIA = "aaaa-mm-dd"`): `desde` y `hasta` son DÍAS
 * como texto, interpretados como días de `America/Santiago`. Acá se reordenan a
 * `dd-mm-aaaa`, que es la forma que `PRD_R4.md:1657` pide en la celda.
 *
 * ⛔⛔ **LA TRAMPA ES ASIMÉTRICA, Y MEDIDA.** `new Date("2026-07-06")` se parsea
 * como medianoche **UTC**, así que formateado en la zona del navegador da:
 *
 *   · `TZ=UTC`             ⇒ `06-07-2026`  ← el día correcto, por casualidad
 *   · `TZ=Europe/Madrid`   ⇒ `06-07-2026`  ← correcto, por casualidad (offset +)
 *   · `TZ=America/Santiago` ⇒ `05-07-2026` ← **EL DÍA ANTERIOR**
 *
 * ⇒ un `new Date(...).toLocaleDateString()` **pasa en UTC y en Madrid y falla en
 * Chile**, que es donde se opera. El reordenamiento por texto da `06-07-2026`
 * **en las tres**, que es lo que `R4-F3-CA-14` (`PRD_R4.md:1839`) exige para
 * «Met Semana 1». Es la razón entera de que esto sea un `split("-")` y no un
 * `Date`.
 *
 * ⚠️ Si el texto no trae la forma esperada se devuelve lo que vino, recortado a
 * diez caracteres: un día raro se dibuja raro —y alguien lo reporta— en vez de
 * volverse `undefined-undefined-undefined`.
 */
export function diaCorto(dia: string): string {
  const [anio, mes, numeroDeDia] = dia.slice(0, 10).split("-")
  if (anio === undefined || mes === undefined || numeroDeDia === undefined) return dia.slice(0, 10)
  return `${numeroDeDia}-${mes}-${anio}`
}

/**
 * La celda «Desde – Hasta» como TEXTO: `dd-mm-aaaa – dd-mm-aaaa`
 * (`PRD_R4.md:1657`).
 *
 * ⚠️ El separador es un guion EN DASH (U+2013) con un espacio a cada lado, que
 * es como el PRD y `R4-F3-CA-14` lo escriben («06-07-2026 – 12-07-2026»).
 */
export function textoDelIntervalo(desde: string, hasta: string): string {
  return `${diaCorto(desde)} – ${diaCorto(hasta)}`
}
